import { useEffect, useMemo, useRef } from 'react'
import Helmet from 'react-helmet'
import { useParams } from 'react-router-dom'

import { ReportBody } from '@/components/reports/body'
import {
  HeaderSeparator,
  ReportDatePicker,
  ReportHeader,
  ReportHeaderDates,
  ReportHeaderMobileActions,
  RunButton,
} from '@/components/reports/header'

import ChartsSection from './components/ChartsSection'
import ReportFilters from './components/ReportFilters'
import ReportTable from './components/ReportTable'
import SaveReportViewButton from './components/SaveReportViewButton'
import { useCustomReportStore } from './store'

function AutoRunReport({ enabled }) {
  const { report: { setAutoRun } } = useCustomReportStore()

  useEffect(() => {
    setAutoRun(enabled)

    return () => setAutoRun(false)
  }, [enabled, setAutoRun])

  return null
}

function Content() {
  const { viewId } = useParams()
  const {
    config: { value: config, setDates },
    report: { run, isLoading },
    views: { items },
    activeView: { setId, apply },
  } = useCustomReportStore()

  const activeView = useMemo(
    () => items.find(item => String(item.id) === String(viewId)),
    [items, viewId],
  )

  const lastAppliedViewIdRef = useRef(null)

  useEffect(() => {
    if (!activeView) {
      setId(null)
      lastAppliedViewIdRef.current = null
      return
    }

    if (lastAppliedViewIdRef.current === activeView.id) return

    lastAppliedViewIdRef.current = activeView.id
    setId(activeView.id)
    apply(activeView.configuration)
  }, [activeView, apply, setId])

  const title = activeView?.description || 'Custom Reports'

  return <>
    <Helmet title={title} />
    <ReportHeader title={title}>
      {viewId ? <>
        <ReportHeaderDates>
          <ReportDatePicker
            dates={{ startDate: config.startDate, endDate: config.endDate }}
            onChange={setDates}
          />
        </ReportHeaderDates>
        <HeaderSeparator />
        <ReportHeaderMobileActions>
          <SaveReportViewButton />
          <RunButton onClick={run} isLoading={isLoading} />
        </ReportHeaderMobileActions>
      </> : null}
    </ReportHeader>
    <ReportBody contentClassName="p-4" loading={isLoading}>
      {viewId ? <>
        <AutoRunReport enabled={Boolean(activeView)} />
        <ReportFilters />
        <ChartsSection />
        <ReportTable />
      </> : <div className="py-12 text-center twy-text-base-muted">
        Create or select a report from the sidebar.
      </div>}
    </ReportBody>
  </>
}

export default function CustomLayout() {
  return <Content />
}
